import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getBlockedAccounts } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Ban, Loader2, RefreshCw, Info, KeyRound, Mail, Clock, Hourglass, XCircle, CircleMinus } from 'lucide-react';

const errMsg = (e, fb) => e?.response?.data?.detail || fb;

const REASON_BADGE = {
  trial_expired: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  payment_past_due: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  canceled: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  inactive: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  pending_email: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
};

// Pagamento atrasado segue a escalada de cores das fases (igual aos banners do cliente):
// moderate (d3-8) laranja → hard (d9-10) vermelho → locked_out (d11+) vermelho intenso.
const PAYMENT_PHASE_BADGE = {
  moderate: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  hard: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  locked_out: 'bg-red-200 text-red-900 dark:bg-red-900/60 dark:text-red-200',
};

function reasonBadgeClass(a) {
  if (a.reason === 'payment_past_due') {
    return PAYMENT_PHASE_BADGE[a.details?.phase] || REASON_BADGE.payment_past_due;
  }
  return REASON_BADGE[a.reason] || REASON_BADGE.inactive;
}
const REASON_VERB = {
  trial_expired: 'Bloqueada',
  payment_past_due: 'Bloqueada',
  canceled: 'Cancelada',
  inactive: 'Desativada',
  pending_email: 'Aguardando confirmação',
};

// Ícone por motivo (cada pill ganha um ícone que corresponde ao seu status).
const REASON_ICON = {
  trial_expired: Hourglass,
  payment_past_due: Clock,
  canceled: XCircle,
  inactive: CircleMinus,
  pending_email: Mail,
};
function ReasonIcon({ reason }) {
  const Icon = REASON_ICON[reason] || Ban;
  return <Icon className="w-3 h-3" />;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return '—';
  }
}
function humanDays(days) {
  if (days == null) return '';
  if (days <= 0) return 'há menos de 1 dia';
  if (days === 1) return 'há 1 dia';
  return `há ${days} dias`;
}

const ISO_DT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const DETAIL_LABELS = {
  canceled_by_user: 'Cancelado pelo usuário',
  subscription_status: 'Status da assinatura',
  phase: 'Fase',
  days_past_due: 'Dias em atraso',
  past_due_since: 'Em atraso desde',
  expired_reason: 'Motivo da expiração',
  messages_used: 'Mensagens usadas',
  messages_limit: 'Limite de mensagens',
  trial_expires_at: 'Trial expirou em',
  origem: 'Origem',
  signup_status: 'Status',
  email_confirmed: 'Email confirmado',
  created_at: 'Criada em',
};
const EXPIRED_REASON_PT = { time: 'tempo', limit: 'limite', time_and_limit: 'tempo + limite' };
const PHASE_PT = { moderate: 'moderado', hard: 'severo', locked_out: 'bloqueada' };
const SUB_STATUS_PT = {
  canceled: 'Cancelada',
  past_due: 'Em atraso',
  unpaid: 'Não paga',
  active: 'Ativa',
  trialing: 'Em teste',
  incomplete: 'Incompleta',
  incomplete_expired: 'Expirada (incompleta)',
};
const SIGNUP_STATUS_PT = { pending_email: 'Aguardando confirmação de email', active: 'Ativo' };

const REASON_FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'trial_expired', label: 'Trial finalizado' },
  { key: 'payment_past_due', label: 'Pagamento atrasado' },
  { key: 'canceled', label: 'Cancelada' },
  { key: 'inactive', label: 'Desativada' },
  { key: 'pending_email', label: 'Aguardando confirmação' },
];

// Cor da pill quando ATIVA — segue a cor do badge de cada motivo ("Todas" = teal/primary).
const FILTER_ACTIVE = {
  all: 'bg-primary text-primary-foreground border-primary',
  trial_expired: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
  payment_past_due: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700',
  canceled: 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700',
  inactive: 'bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600',
  pending_email: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700',
};

function fmtDetailValue(k, v) {
  if (v === null || v === undefined || v === 'None' || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (typeof v === 'string') {
    if (ISO_DT.test(v)) return fmtDateTime(v);
    if (k === 'expired_reason' && EXPIRED_REASON_PT[v]) return EXPIRED_REASON_PT[v];
    if (k === 'phase' && PHASE_PT[v]) return PHASE_PT[v];
    if (k === 'subscription_status' && SUB_STATUS_PT[v]) return SUB_STATUS_PT[v];
    if (k === 'signup_status' && SIGNUP_STATUS_PT[v]) return SIGNUP_STATUS_PT[v];
  }
  return String(v);
}

export default function BlockedAccountsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [reasonFilter, setReasonFilter] = useState('all');

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const a of items) c[a.reason] = (c[a.reason] || 0) + 1;
    return c;
  }, [items]);

  const filtered = useMemo(
    () => (reasonFilter === 'all' ? items : items.filter((a) => a.reason === reasonFilter)),
    [items, reasonFilter],
  );

  const load = useCallback(async (isRefresh) => {
    if (isRefresh) setRefreshing(true);
    try {
      const { data } = await getBlockedAccounts();
      setItems(data.items || []);
    } catch (e) {
      toast.error(errMsg(e, 'Não foi possível carregar as contas bloqueadas.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  return (
    <div className="flex flex-col" data-testid="blocked-accounts-page">
      {/* Cabeçalho (fixo) */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6 shrink-0">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contas Bloqueadas</h1>
          <p className="text-muted-foreground">
            Contas paradas: canceladas, trial finalizado, pagamento em atraso ou desativadas.
          </p>
        </div>
        <Button variant="outline" onClick={() => load(true)} disabled={refreshing}>
          {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Atualizar
        </Button>
      </div>

      <Card className="flex flex-col overflow-hidden">
        <CardHeader className="shrink-0">
          <CardTitle className="flex items-center gap-2">
            <Ban className="w-5 h-5" />
            Contas Bloqueadas
          </CardTitle>
          <CardDescription>
            Contas bloqueadas para envio ou desativadas, em todos os tenants ({items.length}).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Filtro por motivo (fixo) */}
          <div className="px-4 py-3 border-b flex flex-wrap gap-2 shrink-0">
            {REASON_FILTERS.map((f) => {
              const n = counts[f.key] || 0;
              const active = reasonFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setReasonFilter(f.key)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full border transition ${
                    active
                      ? (FILTER_ACTIVE[f.key] || FILTER_ACTIVE.all)
                      : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                  }`}
                  data-testid={`blocked-filter-${f.key}`}
                >
                  {f.label} <span className="opacity-70">({n})</span>
                </button>
              );
            })}
          </div>
          <div className="[&>div]:max-h-[580px] [&>div]:overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead>Conta</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Criada em</TableHead>
                  <TableHead>Status desde</TableHead>
                  <TableHead>Instância</TableHead>
                  <TableHead className="text-right">Detalhes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">
                      {items.length === 0
                        ? 'Nenhuma conta bloqueada no momento 🎉'
                        : 'Nenhuma conta para este motivo.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((a) => (
                    <TableRow key={a.tenant_id} className="hover:bg-primary/5">
                      <TableCell className="py-2">
                        <div className="font-medium text-sm">{a.name}</div>
                        <div className="text-xs text-muted-foreground break-all">{a.email}</div>
                      </TableCell>
                      <TableCell className="py-2 text-sm">{a.plan}</TableCell>
                      <TableCell className="py-2">
                        <Badge variant="outline" className={`${reasonBadgeClass(a)} gap-1 border-transparent shadow`}>
                          <ReasonIcon reason={a.reason} />
                          {a.reason_label}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-sm whitespace-nowrap">{fmtDate(a.created_at)}</TableCell>
                      <TableCell className="py-2 text-sm whitespace-nowrap">
                        <div className="font-medium">
                          {REASON_VERB[a.reason] || 'Parada'} {humanDays(a.days_in_status)}
                        </div>
                        <div className="text-xs text-muted-foreground">{fmtDateTime(a.since)}</div>
                      </TableCell>
                      <TableCell className="py-2">
                        {a.slot_occupied ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 whitespace-nowrap">
                            <KeyRound className="w-3 h-3" />
                            Ativa{a.pat_label ? ` · ${a.pat_label}` : ''}
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full border border-dashed border-muted-foreground/30 text-muted-foreground whitespace-nowrap">
                            Livre
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" title="Ver detalhes">
                              <Info className="w-4 h-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72" align="end">
                            <p className="text-sm font-semibold mb-1">Detalhes</p>
                            <dl className="text-xs space-y-1">
                              <DetailRow k="Tenant ID" v={a.tenant_id} mono />
                              {Object.entries(a.details || {}).map(([k, v]) => (
                                <DetailRow key={k} k={DETAIL_LABELS[k] || k} v={fmtDetailValue(k, v)} />
                              ))}
                            </dl>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DetailRow({ k, v, mono }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={`text-right break-all ${mono ? 'font-mono' : ''}`}>{v}</dd>
    </div>
  );
}
